import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OUTLET_COLOR, ENDPOINT_COLOR, colorForIndex } from "../../lib/routeColors";

const DELIVERED_COLOR = "#16A34A";
const NOT_DELIVERED_COLOR = "#DC2626";
const PACKING_SKIPPED_COLOR = "#D97706";
const LIVE_COLOR = "#2563EB";
const iconCache = new Map();

const CAR_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>`;

function divIcon(color, label, shape = "circle", size = 22, badge = false) {
  const key = `${color}|${label}|${shape}|${size}|${badge}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const radius = shape === "circle" ? "50%" : "4px";
  const badgeHtml = badge
    ? `<div title="Skipped by packing team" style="position:absolute;top:-3px;right:-3px;width:10px;height:10px;border-radius:50%;background:${PACKING_SKIPPED_COLOR};border:1.5px solid white;box-shadow:0 0 0 1px rgba(0,0,0,0.15);"></div>`
    : "";
  const icon = L.divIcon({
    className: "",
    html: `<div style="position:relative;background:${color};width:${size}px;height:${size}px;border-radius:${radius};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;color:white;font-size:10px;font-weight:700;font-family:IBM Plex Sans,sans-serif;">${label || ""}${badgeHtml}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
  iconCache.set(key, icon);
  return icon;
}

function carIcon() {
  const key = "car-live";
  if (iconCache.has(key)) return iconCache.get(key);
  const icon = L.divIcon({
    className: "",
    html: `<div data-testid="route-map-live-driver-marker" style="position:relative;width:28px;height:28px;">
      <div style="position:absolute;inset:0;border-radius:50%;background:${LIVE_COLOR};opacity:0.35;" class="animate-ping"></div>
      <div style="position:relative;width:28px;height:28px;border-radius:50%;background:${LIVE_COLOR};border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;">${CAR_SVG}</div>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
  iconCache.set(key, icon);
  return icon;
}

function timeAgo(epochSecondsStr) {
  const n = Number(epochSecondsStr);
  if (!n) return "never";
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - n);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function FitBounds({ points }) {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    const key = points.map((p) => p.join(",")).join("|");
    if (points.length === 0 || lastKey.current === key) return;
    try {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 });
      lastKey.current = key;
    } catch {
      /* invalid bounds, ignore */
    }
  }, [points, map]);
  return null;
}

function toLatLng(lat, lon) {
  const la = parseFloat(lat);
  const lo = parseFloat(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo) || la === 0 || lo === 0) return null;
  return [la, lo];
}

export function RouteMap({ outlet, driver, stops, live, trackingEnabled, roadLegs }) {
  const driverColor = driver?.color || colorForIndex(driver?.id ?? 0);
  const outletPt = toLatLng(outlet?.latitude, outlet?.longitude);
  const driverPt = toLatLng(driver?.latitude, driver?.longitude);
  const livePt = toLatLng(live?.lat, live?.lng);
  const validStops = stops
    .map((s) => ({ ...s, pt: toLatLng(s.latitude, s.longitude) }))
    .filter((s) => s.pt);
  const allPoints = [...(outletPt ? [outletPt] : []), ...validStops.map((s) => s.pt), ...(driverPt ? [driverPt] : [])];

  if (allPoints.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-text-muted" data-testid="route-map-no-data">
        No location data available to plot this route.
      </div>
    );
  }

  // Ordered path nodes with a "delivered" flag so we can paint the traveled (delivered) portion of
  // the route green, segment by segment, up to wherever the driver actually is in the run.
  const pathNodes = [
    ...(outletPt ? [{ pt: outletPt, delivered: true }] : []),
    ...validStops.map((s) => ({ pt: s.pt, delivered: s.delivery_status === 2 })),
    ...(driverPt ? [{ pt: driverPt, delivered: validStops.length > 0 && validStops[validStops.length - 1].delivery_status === 2 }] : []),
  ];
  const segments = [];
  for (let i = 0; i < pathNodes.length - 1; i++) {
    segments.push({ from: pathNodes[i].pt, to: pathNodes[i + 1].pt, green: pathNodes[i + 1].delivered });
  }

  return (
    <div className="relative h-full w-full" data-testid="route-detail-map">
      <MapContainer center={allPoints[0]} zoom={12} style={{ height: "100%", width: "100%" }}>
        <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitBounds points={allPoints} />

        {segments.map((seg, i) => {
          // Use the road-following geometry for this leg when available
          // (roadLegs[i] aligns 1:1 with segment i: outlet->stop1, stop1->stop2, ...);
          // otherwise draw the straight segment (OSRM unavailable / driver-live tail).
          const leg = Array.isArray(roadLegs) ? roadLegs[i] : null;
          const positions = leg && leg.length >= 2 ? leg : [seg.from, seg.to];
          return (
            <Polyline
              key={i}
              positions={positions}
              pathOptions={{ color: seg.green ? DELIVERED_COLOR : driverColor, weight: 4, opacity: 0.85 }}
            />
          );
        })}

        {outletPt && (
          <Marker position={outletPt} icon={divIcon(OUTLET_COLOR, "R", "square", 26)}>
            <Popup><p className="text-xs font-semibold">Restaurant — {outlet.name}</p></Popup>
          </Marker>
        )}

        {validStops.map((s, idx) => {
          const color = s.delivery_status === 2 ? DELIVERED_COLOR : s.delivery_status === 3 ? NOT_DELIVERED_COLOR : driverColor;
          return (
            <Marker key={s.id} position={s.pt} icon={divIcon(color, String(s.delivery_order_id ?? idx + 1), "circle", 22, s.packing_status === 2)}>
              <Popup>
                <div className="text-xs space-y-0.5" data-testid={`route-map-popup-${s.id}`}>
                  <p className="font-semibold text-text-main">{s.customer_name || `Delivery #${s.id}`}</p>
                  <p className="text-text-muted">{s.address || "No address"}</p>
                  <p className="text-text-muted">Stop {s.delivery_order_id ?? idx + 1}</p>
                  {s.packing_status === 2 && (
                    <p className="font-semibold" style={{ color: PACKING_SKIPPED_COLOR }}>Skipped by packing team</p>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {driverPt && (
          <Marker position={driverPt} icon={divIcon(ENDPOINT_COLOR, "D", "square", 24)}>
            <Popup><p className="text-xs font-semibold">Driver Endpoint</p></Popup>
          </Marker>
        )}

        {trackingEnabled && livePt && (
          <Marker position={livePt} icon={carIcon()} data-testid="route-map-live-driver-marker">
            <Popup><p className="text-xs font-semibold">Driver is here — last ping {timeAgo(live?.at)}</p></Popup>
          </Marker>
        )}
      </MapContainer>

      <div
        className="absolute bottom-2 left-2 z-[400] flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface/95 px-3 py-1.5 text-[11px] text-text-muted shadow-sm"
        data-testid="route-map-legend"
      >
        <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded-sm text-[9px] font-bold text-white" style={{ background: OUTLET_COLOR }}>R</span>Restaurant</span>
        <span className="flex items-center gap-1.5" data-testid="route-map-legend-driver-color"><span className="flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white" style={{ background: driverColor }}>#</span>{driver?.name ? `${driver.name}'s route` : "Pending stop"}</span>
        <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white" style={{ background: DELIVERED_COLOR }}>#</span>Delivered</span>
        <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white" style={{ background: NOT_DELIVERED_COLOR }}>#</span>Not delivered</span>
        <span className="flex items-center gap-1.5" data-testid="route-map-legend-packing-skipped"><span className="flex h-4 w-4 items-center justify-center rounded-full" style={{ background: PACKING_SKIPPED_COLOR }} />Skipped by packing</span>
        <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded-sm text-[9px] font-bold text-white" style={{ background: ENDPOINT_COLOR }}>D</span>Driver endpoint</span>
        {trackingEnabled && livePt && (
          <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded-full" style={{ background: LIVE_COLOR }} />Live location</span>
        )}
      </div>
    </div>
  );
}
